/* GalleryViewer: mobile-first lightbox for PDP product imagery.

   Features:
   - Pinch-zoom (two-finger), focal point preserved
   - Double-tap to zoom in/out at the tap location
   - Swipe left/right between images at base scale
   - Drag to pan when zoomed
   - Wheel zoom around the cursor (desktop)
   - Keyboard: arrows to navigate, +/- to zoom, 0 to reset

   Depends on `theme.addDelegateEventListener` from assets/main.js, which is
   loaded before this file. Registered as a custom element so the snippet
   `gallery-viewer.liquid` can place a <gallery-viewer> in the DOM. */

/* eslint-disable max-len, class-methods-use-this */
(function () {
  if (window.customElements.get('gallery-viewer')) return;

  const GalleryViewer = class extends HTMLElement {
    connectedCallback() {
      if (!this.initialised) {
        this.initialised = true;
        this.classList.add('gallery-viewer--pre-reveal');

        this.stage = this.querySelector('.gallery-viewer__stage');
        this.zoomContainer = this.querySelector('.gallery-viewer__zoom-container');
        this.thumbContainer = this.querySelector('.gallery-viewer__thumbs');
        this.previousBtn = this.querySelector('.gallery-viewer__prev');
        this.nextBtn = this.querySelector('.gallery-viewer__next');
        this.counterCurrent = this.querySelector('.gallery-viewer__counter-current');
        this.counterTotal = this.querySelector('.gallery-viewer__counter-total');

        // image transform state (scale + translate from container center)
        this.image = null;
        this.naturalW = 0;
        this.naturalH = 0;
        this.baseScale = 1;
        this.scale = 1;
        this.tx = 0;
        this.ty = 0;
        this.zoomCap = 4;

        // gesture state
        this.pointers = new Map();
        this.gesture = null;
        this.lastTap = { time: 0, x: 0, y: 0 };
        this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

        theme.addDelegateEventListener(this, 'click', '.gallery-viewer__thumb', this.onThumbClick.bind(this));
        this.previousBtn.addEventListener('click', this.selectPreviousThumb.bind(this));
        this.nextBtn.addEventListener('click', this.selectNextThumb.bind(this));

        this.zoomContainer.style.touchAction = 'none';
        this.zoomContainer.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.zoomContainer.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.zoomContainer.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.zoomContainer.addEventListener('pointercancel', this.onPointerUp.bind(this));
        this.zoomContainer.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
        this.zoomContainer.addEventListener('dragstart', (e) => e.preventDefault());

        new ResizeObserver(() => this.fit()).observe(this.zoomContainer);
      }

      this.addEventListener('keyup', this.handleKeyup.bind(this));
      requestAnimationFrame(() => this.classList.remove('gallery-viewer--pre-reveal'));
    }

    init(currentFullUrl) {
      const match = [...this.thumbContainer.children].find((el) => el.dataset.zoomUrl === currentFullUrl);
      this.selectThumb(match || this.thumbContainer.firstElementChild);
    }

    getVisibleThumbs() {
      return [...this.thumbContainer.children].filter((el) => !el.hidden);
    }

    updateCounter(thumb) {
      if (!this.counterCurrent || !this.counterTotal) return;
      const visible = this.getVisibleThumbs();
      const idx = visible.indexOf(thumb);
      this.counterCurrent.textContent = String(Math.max(0, idx) + 1);
      this.counterTotal.textContent = String(visible.length);
    }

    selectThumb(thumb) {
      if (!thumb) return;
      [...thumb.parentElement.children].forEach((el) => {
        el.classList.toggle('gallery-viewer__thumb--active', el === thumb);
        el.setAttribute('aria-selected', el === thumb ? 'true' : 'false');
      });
      thumb.scrollIntoView({ behavior: this.reducedMotion ? 'auto' : 'smooth', block: 'nearest', inline: 'center' });
      this.updateCounter(thumb);

      this.zoomContainer.classList.add('gallery-viewer__zoom-container--loading');
      const img = document.createElement('img');
      img.className = 'gallery-viewer__image';
      img.alt = '';
      img.draggable = false;
      img.style.visibility = 'hidden';
      img.addEventListener('load', () => {
        this.zoomContainer.classList.remove('gallery-viewer__zoom-container--loading');
        img.style.visibility = '';
        this.naturalW = img.naturalWidth;
        this.naturalH = img.naturalHeight;
        this.fit();
      }, { once: true });
      img.src = thumb.dataset.zoomUrl;
      this.image = img;
      this.zoomContainer.replaceChildren(img);
    }

    selectPreviousThumb(evt) {
      if (evt) evt.preventDefault();
      const visible = this.getVisibleThumbs();
      if (visible.length < 2) return;
      const active = this.thumbContainer.querySelector('.gallery-viewer__thumb--active');
      const idx = Math.max(0, visible.indexOf(active));
      this.selectThumb(visible[(idx - 1 + visible.length) % visible.length]);
    }

    selectNextThumb(evt) {
      if (evt) evt.preventDefault();
      const visible = this.getVisibleThumbs();
      if (visible.length < 2) return;
      const active = this.thumbContainer.querySelector('.gallery-viewer__thumb--active');
      const idx = Math.max(0, visible.indexOf(active));
      this.selectThumb(visible[(idx + 1) % visible.length]);
    }

    fit() {
      if (!this.image || !this.naturalW) return;
      const cw = this.zoomContainer.clientWidth;
      const ch = this.zoomContainer.clientHeight;
      if (cw === 0 || ch === 0) return;
      this.baseScale = Math.min(cw / this.naturalW, ch / this.naturalH, 1);
      this.scale = this.baseScale;
      this.tx = 0;
      this.ty = 0;
      this.image.style.width = `${this.naturalW}px`;
      this.image.style.height = `${this.naturalH}px`;
      this.image.style.left = `${(cw - this.naturalW) / 2}px`;
      this.image.style.top = `${(ch - this.naturalH) / 2}px`;
      this.applyTransform();
      this.classList.toggle('gallery-viewer--zoomable', this.baseScale < 1);
    }

    applyTransform(animate = false) {
      if (!this.image) return;
      this.image.style.transition = animate && !this.reducedMotion ? 'transform 220ms cubic-bezier(.2,.7,.3,1)' : 'none';
      this.image.style.transform = `translate3d(${this.tx}px, ${this.ty}px, 0) scale(${this.scale})`;
      this.classList.toggle('gallery-viewer--zoomed', this.scale > this.baseScale + 1e-3);
    }

    clampPan() {
      const cw = this.zoomContainer.clientWidth;
      const ch = this.zoomContainer.clientHeight;
      const imgW = this.naturalW * this.scale;
      const imgH = this.naturalH * this.scale;
      const maxX = Math.max(0, (imgW - cw) / 2);
      const maxY = Math.max(0, (imgH - ch) / 2);
      this.tx = Math.max(-maxX, Math.min(maxX, this.tx));
      this.ty = Math.max(-maxY, Math.min(maxY, this.ty));
    }

    setScaleAround(newScale, focalClientX, focalClientY) {
      const minScale = this.baseScale;
      const maxScale = Math.max(this.baseScale * this.zoomCap, 1);
      const clamped = Math.max(minScale, Math.min(maxScale, newScale));
      const rect = this.zoomContainer.getBoundingClientRect();
      const fx = (focalClientX - rect.left) - rect.width / 2;
      const fy = (focalClientY - rect.top) - rect.height / 2;
      const ratio = clamped / this.scale;
      this.tx = fx - (fx - this.tx) * ratio;
      this.ty = fy - (fy - this.ty) * ratio;
      this.scale = clamped;
      this.clampPan();
      this.applyTransform();
    }

    toggleZoomAt(clientX, clientY) {
      if (this.scale > this.baseScale + 1e-3) {
        this.scale = this.baseScale;
        this.tx = 0; this.ty = 0;
        this.applyTransform(true);
      } else {
        const target = Math.max(1, this.baseScale * 2.5);
        const prevTx = this.tx;
        const prevTy = this.ty;
        const prevScale = this.scale;
        this.setScaleAround(target, clientX, clientY);
        const nextTx = this.tx;
        const nextTy = this.ty;
        const nextScale = this.scale;
        this.tx = prevTx; this.ty = prevTy; this.scale = prevScale;
        this.applyTransform();
        requestAnimationFrame(() => {
          this.tx = nextTx; this.ty = nextTy; this.scale = nextScale;
          this.applyTransform(true);
        });
      }
    }

    onPointerDown(evt) {
      if (evt.button !== undefined && evt.button > 0) return;
      try { this.zoomContainer.setPointerCapture(evt.pointerId); } catch (e) { /* noop */ }
      this.pointers.set(evt.pointerId, { x: evt.clientX, y: evt.clientY, startX: evt.clientX, startY: evt.clientY });

      if (this.pointers.size === 1) {
        this.gesture = {
          mode: 'pending',
          startTx: this.tx,
          startTy: this.ty,
          moved: false,
          pointerId: evt.pointerId
        };
      } else if (this.pointers.size === 2) {
        const arr = [...this.pointers.values()];
        const dist = Math.hypot(arr[0].x - arr[1].x, arr[0].y - arr[1].y);
        this.gesture = {
          mode: 'pinch',
          startDist: dist || 1,
          startScale: this.scale,
          moved: true
        };
      }
    }

    onPointerMove(evt) {
      const p = this.pointers.get(evt.pointerId);
      if (!p) return;
      p.x = evt.clientX;
      p.y = evt.clientY;
      if (!this.gesture) return;

      if (this.gesture.mode === 'pinch' && this.pointers.size >= 2) {
        const arr = [...this.pointers.values()];
        const newDist = Math.hypot(arr[0].x - arr[1].x, arr[0].y - arr[1].y);
        const focalX = (arr[0].x + arr[1].x) / 2;
        const focalY = (arr[0].y + arr[1].y) / 2;
        const targetScale = this.gesture.startScale * (newDist / this.gesture.startDist);
        this.setScaleAround(targetScale, focalX, focalY);
        return;
      }

      if (this.pointers.size === 1) {
        const dx = p.x - p.startX;
        const dy = p.y - p.startY;
        if (this.gesture.mode === 'pending') {
          if (Math.abs(dx) < 6 && Math.abs(dy) < 6) return;
          if (this.scale > this.baseScale + 1e-3) {
            this.gesture.mode = 'pan';
          } else if (Math.abs(dx) > Math.abs(dy)) {
            this.gesture.mode = 'swipe';
          } else {
            this.gesture.mode = 'idle';
          }
        }

        if (this.gesture.mode === 'pan') {
          this.tx = this.gesture.startTx + dx;
          this.ty = this.gesture.startTy + dy;
          this.clampPan();
          this.applyTransform();
          this.gesture.moved = true;
        } else if (this.gesture.mode === 'swipe') {
          this.tx = dx;
          this.applyTransform();
          this.gesture.moved = true;
          this.gesture.swipeDx = dx;
        }
      }
    }

    onPointerUp(evt) {
      const p = this.pointers.get(evt.pointerId);
      if (!p) return;
      const endX = p.x;
      const endY = p.y;
      this.pointers.delete(evt.pointerId);
      try { this.zoomContainer.releasePointerCapture(evt.pointerId); } catch (e) { /* noop */ }

      if (!this.gesture) return;

      // End of pinch
      if (this.gesture.mode === 'pinch') {
        if (this.pointers.size === 1) {
          // promote remaining finger to pan/swipe gesture
          const remaining = [...this.pointers.values()][0];
          remaining.startX = remaining.x;
          remaining.startY = remaining.y;
          this.gesture = {
            mode: this.scale > this.baseScale + 1e-3 ? 'pan' : 'idle',
            startTx: this.tx,
            startTy: this.ty,
            moved: false
          };
          return;
        }
        // both up
        if (this.scale < this.baseScale) {
          this.scale = this.baseScale;
          this.tx = 0; this.ty = 0;
          this.applyTransform(true);
        }
        this.gesture = null;
        return;
      }

      // End of swipe
      if (this.gesture.mode === 'swipe') {
        const cw = this.zoomContainer.clientWidth;
        const dx = this.gesture.swipeDx || 0;
        const threshold = Math.min(80, cw * 0.18);
        const visibleCount = this.getVisibleThumbs().length;
        if (Math.abs(dx) > threshold && visibleCount > 1) {
          const dir = dx < 0 ? 1 : -1; // swipe left => next
          this.tx = -dir * cw;
          this.applyTransform(true);
          setTimeout(() => {
            if (dir > 0) this.selectNextThumb();
            else this.selectPreviousThumb();
          }, 180);
        } else {
          this.tx = 0;
          this.applyTransform(true);
        }
        this.gesture = null;
        return;
      }

      // End of pan: no further action needed (transform already current)
      if (this.gesture.mode === 'pan') {
        this.gesture = null;
        return;
      }

      // Tap (single or double)
      if ((this.gesture.mode === 'pending' || this.gesture.mode === 'idle') && !this.gesture.moved) {
        const now = Date.now();
        const dx = endX - this.lastTap.x;
        const dy = endY - this.lastTap.y;
        const isDouble = now - this.lastTap.time < 300 && Math.abs(dx) < 30 && Math.abs(dy) < 30;
        this.lastTap = { time: isDouble ? 0 : now, x: endX, y: endY };
        if (isDouble) this.toggleZoomAt(endX, endY);
      }
      this.gesture = null;
    }

    onWheel(evt) {
      evt.preventDefault();
      const factor = Math.exp(-evt.deltaY * 0.0015);
      this.setScaleAround(this.scale * factor, evt.clientX, evt.clientY);
    }

    onThumbClick(evt, thumb) {
      evt.preventDefault();
      this.selectThumb(thumb);
    }

    handleKeyup(evt) {
      if (evt.key === 'ArrowLeft') { evt.preventDefault(); this.selectPreviousThumb(); }
      else if (evt.key === 'ArrowRight') { evt.preventDefault(); this.selectNextThumb(); }
      else if (evt.key === '0') {
        this.scale = this.baseScale; this.tx = 0; this.ty = 0;
        this.applyTransform(true);
      } else if (evt.key === '+' || evt.key === '=') {
        const r = this.zoomContainer.getBoundingClientRect();
        this.setScaleAround(this.scale * 1.25, r.left + r.width / 2, r.top + r.height / 2);
      } else if (evt.key === '-' || evt.key === '_') {
        const r = this.zoomContainer.getBoundingClientRect();
        this.setScaleAround(this.scale / 1.25, r.left + r.width / 2, r.top + r.height / 2);
      }
    }
  };

  window.customElements.define('gallery-viewer', GalleryViewer);
})();
